import { useGetMetricGroupsQuery } from '@api/metric-groups';
import { cloneDeep } from '@utils/object';
import { useGetSurveysQuery } from '@apps/materiality-tracker/api/materiality-assessment';
import { initializeMetricGroupFromSurvey } from '@apps/materiality-tracker/components/metric-groups/utils';
import { AssignmentViewMode, ViewMode } from '@features/custom-metrics/constants';
import { useSearchParams } from '@hooks/useSearchParams';
import CustomMetricContainer from '@features/custom-metrics/CustomMetricContainer';
import { DEFAULT_CUSTOM_METRICS_USAGE } from '@features/custom-metrics/utils';
import Dashboard from '@g17eco/molecules/dashboard';
import { Loader } from '@g17eco/atoms/loader';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { useAppSelector } from '@reducers/index';
import { skipToken } from '@reduxjs/toolkit/query';
import NotAuthorised from '@routes/not-authorised';
import { canManageCurrentLevel } from '@selectors/user';
import { isMTMetricGroup } from '@utils/metricGroup';
import { useParams } from 'react-router-dom';
import { MetricGroupDashboard } from '../components/metric-groups/MetricGroupDashboard';
import { MetricGroupForm } from '../components/metric-groups/MetricGroupForm';

interface RouteParams {
  initiativeId: string;
  groupId?: string;
  viewMode?: ViewMode;
}

const getAssignmentViewMode = (mode: string | null): AssignmentViewMode => {
  switch (mode) {
    case AssignmentViewMode.SubModules:
    case AssignmentViewMode.Questions:
      return mode;
    default:
      return AssignmentViewMode.SubModules;
  }
};

export const CustomMetricGroupRoute = () => {
  const { initiativeId, groupId, viewMode } = useParams<RouteParams>();

  const searchParams = useSearchParams();
  const assignmentViewMode = getAssignmentViewMode(searchParams.get('assignmentViewMode'));
  const subgroupId = searchParams.get('subGroupId');

  const canManageMetricGroup = useAppSelector(canManageCurrentLevel);
  const { data: surveys, isFetching: isSurveysLoading } = useGetSurveysQuery(
    canManageMetricGroup ? { initiativeId } : skipToken,
  );
  const {
    data: metricGroups,
    isFetching: isMetricGroupsLoading,
    refetch,
  } = useGetMetricGroupsQuery(canManageMetricGroup ? initiativeId : skipToken);

  const isLoading = isSurveysLoading || isMetricGroupsLoading;

  const { existingSurveyIds, MTMetricGroups } = (metricGroups || []).reduce<{
    existingSurveyIds: string[];
    MTMetricGroups: MetricGroup[];
  }>(
    (acc, metricGroup) => {
      const surveyId = metricGroup.source?.surveyId;
      if (isMTMetricGroup(metricGroup) && surveyId) {
        acc.MTMetricGroups.push(cloneDeep(metricGroup));
        acc.existingSurveyIds.push(surveyId);
      }
      return acc;
    },
    { existingSurveyIds: [], MTMetricGroups: [] },
  );

  const extendedMetricGroups = (surveys || []).reduce<MetricGroup[]>((acc, survey) => {
    if (!existingSurveyIds.includes(survey._id)) {
      acc.push(initializeMetricGroupFromSurvey(survey));
    }
    return acc;
  }, MTMetricGroups);

  if (!canManageMetricGroup) {
    return <NotAuthorised />;
  }

  const handleReload = async () => {
    refetch();
  };

  const renderView = () => {
    if (groupId) {
      const metricGroup = extendedMetricGroups.find((group) => group._id === groupId);
      const subGroup = metricGroup?.subgroups?.find((group) => group._id === subgroupId);
      return metricGroup ? (
        <MetricGroupForm
          initiativeId={initiativeId}
          metricGroup={metricGroup}
          viewMode={viewMode}
          assignmentViewMode={assignmentViewMode}
          subGroup={subGroup}
          handleReload={handleReload}
        />
      ) : null;
    }
    return <MetricGroupDashboard initiativeId={initiativeId} metricGroups={extendedMetricGroups} />;
  };

  return (
    <CustomMetricContainer
      initiativeId={initiativeId}
      view={viewMode}
      groupId={groupId}
      isPortfolioTracker={false}
      customMetricsUsage={DEFAULT_CUSTOM_METRICS_USAGE}
      metricGroups={extendedMetricGroups}
    >
      {isLoading ? <Loader /> : null}
      <Dashboard>{renderView()}</Dashboard>
    </CustomMetricContainer>
  );
};
